<template>
  <v-dialog
    :model-value="modelValue"
    max-width="500"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card class="profile-dialog">
      <v-card-text class="pa-6">
        <div class="d-flex flex-column align-center mb-6">
          <v-avatar
            size="120"
            class="bg-primary mb-4 elevation-2"
          >
            <span class="text-h3 grey-lighten-3">{{ userInitials }}</span>
          </v-avatar>
          <h2 class="text-h5 font-weight-medium mb-1">
            {{ user?.getFullName() }}
          </h2>
          <p class="text-subtitle-1 text-medium-emphasis">
            {{ user?.email }}
          </p>
        </div>

        <v-divider class="mb-6" />

        <v-expansion-panels
          variant="accordion"
          class="profile-panels"
        >
          <v-expansion-panel elevation="0">
            <v-expansion-panel-title class="text-subtitle-1 bg-surface">
              <template #default="{ expanded }">
                <v-row no-gutters>
                  <v-col
                    cols="12"
                    class="d-flex align-center"
                  >
                    <v-icon
                      :icon="expanded ? 'mdi-key' : 'mdi-key-outline'"
                      class="me-2"
                      color="primary"
                    />
                    Change Password
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="bg-surface">
              <v-form @submit.prevent="handlePasswordChange">
                <v-text-field
                  v-model="passwordForm.new_password1"
                  label="New Password"
                  type="password"
                  required
                  variant="outlined"
                  density="comfortable"
                  class="mb-4"
                  hide-details
                />
                <v-text-field
                  v-model="passwordForm.new_password2"
                  label="Confirm Password"
                  type="password"
                  required
                  variant="outlined"
                  density="comfortable"
                  hide-details
                />
                <div class="d-flex justify-end mt-6">
                  <v-btn
                    color="primary"
                    :loading="isSubmitting"
                    prepend-icon="mdi-check"
                    @click="handlePasswordChange"
                  >
                    Update Password
                  </v-btn>
                </div>
              </v-form>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          color="primary"
          variant="text"
          @click="emit('update:modelValue', false)"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'

interface Props {
  modelValue: boolean
}

defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const authStore = useAuthStore()
const { getUser } = storeToRefs(authStore)

const isSubmitting = ref(false)
const passwordForm = ref({
  new_password1: '',
  new_password2: ''
})

const user = computed(() => getUser.value)
const userInitials = computed(() => user.value?.getInitials() ?? '')

const handlePasswordChange = async () => {
  try {
    isSubmitting.value = true
    await authStore.changePassword(
      passwordForm.value.new_password1,
      passwordForm.value.new_password2
    )
    passwordForm.value = {
      new_password1: '',
      new_password2: ''
    }
  } catch (error) {
    console.error('Password change failed:', error)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.profile-dialog :deep(.v-expansion-panel) {
  background: transparent;
}

.profile-dialog :deep(.v-expansion-panel-title) {
  padding: 16px;
  border-radius: 8px;
}

.profile-dialog :deep(.v-expansion-panel-text__wrapper) {
  padding: 20px;
}

.profile-dialog :deep(.v-expansion-panel--active > .v-expansion-panel-title) {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.profile-panels {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
}
</style> 